import { useEffect } from "react";
import { Alert, Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import typography from "../../../../cssUtils/typography";
import { ColorsConstants } from "../../../../cssUtils/constants";
import AppChip from "../../../../common/app-chip";

const STAGE_OPTIONS = [
  { value: "reminder_1", label: "Reminder 1" },
  { value: "reminder_2", label: "Reminder 2" },
  { value: "reminder_3", label: "Reminder 3" },
  { value: "reminder_4", label: "Reminder 4" },
  { value: "reminder_5", label: "Reminder 5" },
  { value: "reminder_6", label: "Reminder 6" },
];

const YES_NO_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const TIMING_DIRECTION_OPTIONS = [
  { value: "before", label: "Days before due" },
  { value: "after", label: "Days after due" },
];

const EMI_FIELD_OPTIONS = [
  { value: "customer_name", label: "Customer Name" },
  { value: "due_date", label: "Due Date" },
  { value: "tenant_name", label: "Tenant Name" },
];

// Sequential {{1}}, {{2}}, ... count, matching how the backend
// (templates.gupshup_helper.get_required_fields) counts body placeholders.
const getPlaceholderCount = (template) => {
  const content = template?.template_json?.content || "";
  let count = 0;
  while (content.includes(`{{${count + 1}}}`)) count += 1;
  return count;
};

// The backend (emi_reminder/utils.py send_reminder_template) can fill in text
// and image templates only; any other header type fails to send.
const UNSUPPORTED_TEMPLATE_TYPES = ["VIDEO", "DOCUMENT", "LOCATION", "GIF", "CAROUSEL"];

const getTemplateType = (template) =>
  (template?.template_json?.templateType || "").toUpperCase();

const isUnsupportedTemplate = (template) =>
  UNSUPPORTED_TEMPLATE_TYPES.includes(getTemplateType(template));

const EMI_SAMPLE_VALUES = {
  customer_name: "Rahul Sharma",
  due_date: "10/10/2026",
  tenant_name: "Cent Bank",
};

const buildPreviewText = (template, mapping) => {
  let content = template?.template_json?.content || "";
  mapping.forEach((field, index) => {
    const sample = EMI_SAMPLE_VALUES[field] || `{{${index + 1}}}`;
    content = content.split(`{{${index + 1}}}`).join(sample);
  });
  return content;
};

const SendEmiReminderPreset = ({ config, handleConfigChange, templates }) => {
  const selectedTemplate =
    templates?.find((t) => t?.template_id === config["template_id"]) || null;
  const placeholderCount = getPlaceholderCount(selectedTemplate);
  const selectedTemplateType = getTemplateType(selectedTemplate);
  const isImageTemplate = selectedTemplateType === "IMAGE";
  const isUnsupportedSelection = isUnsupportedTemplate(selectedTemplate);
  const variableMapping = config["variable_mapping"] || [];
  // Reminder 1 is mandatory and always enabled regardless of its own config value.
  const isEnabled = config["stage"] === "reminder_1" ? true : !!config["enabled"];

  const handleTemplateChange = (newValue) => {
    handleConfigChange("template_id", newValue?.template_id);
    // Re-sync mapping length to the newly selected template's placeholder count.
    const nextCount = getPlaceholderCount(newValue);
    const next = Array.from(
      { length: nextCount },
      (_, i) => variableMapping[i] || "",
    );
    handleConfigChange("variable_mapping", next);
  };

  const handleMappingChange = (index, value) => {
    const next = [...variableMapping];
    next[index] = value;
    handleConfigChange("variable_mapping", next);
  };

  const handleStageChange = (newStage) => {
    handleConfigChange("stage", newStage);
    // Reminder 1 is mandatory and always enabled — its Enabled toggle is locked
    // to Yes in the UI below, so make sure that's actually saved, not just shown.
    if (newStage === "reminder_1") {
      handleConfigChange("enabled", true);
    }
  };

  // Backfill actual config to match what's already shown on screen, same reason
  // as SendSipReminderPreset: the backend's config_schema requires "enabled"
  // (and "timing_direction" once enabled) to actually be present in config.
  useEffect(() => {
    if (config["enabled"] === undefined) {
      handleConfigChange("enabled", config["stage"] === "reminder_1");
    }
    if (config["timing_direction"] === undefined) {
      handleConfigChange("timing_direction", "before");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return (
    <Stack spacing={3}>
      <Autocomplete
        options={STAGE_OPTIONS}
        value={STAGE_OPTIONS.find((o) => o.value === config["stage"]) || null}
        onChange={(e, newValue) => handleStageChange(newValue?.value)}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        renderInput={(params) => (
          <TextField {...params} label="Reminder Stage" variant="outlined" required />
        )}
        noOptionsText="No stages found"
      />

      <Autocomplete
        options={YES_NO_OPTIONS}
        value={
          YES_NO_OPTIONS.find(
            (o) => o.value === (config["stage"] === "reminder_1" ? true : !!config["enabled"]),
          ) || null
        }
        disabled={config["stage"] === "reminder_1"}
        onChange={(e, newValue) => handleConfigChange("enabled", !!newValue?.value)}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Enabled"
            variant="outlined"
            required
            helperText={
              config["stage"] === "reminder_1" ? "Reminder 1 is mandatory and always enabled" : ""
            }
          />
        )}
        noOptionsText="No options found"
      />

      {!isEnabled && (
        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.grayFontColor }}>
          This reminder is disabled — no configuration is needed while it's off.
        </Typography>
      )}

      {isEnabled && (
        <>
          <Stack spacing={1.5}>
            <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
              Reminder Timing
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Send reminder"
                type="number"
                variant="outlined"
                value={config["timing_days"] ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  handleConfigChange("timing_days", raw === "" ? null : Math.max(0, Number(raw)));
                }}
                helperText="Example: 10 Days before due"
                sx={{ flex: 1 }}
              />
              <Autocomplete
                options={TIMING_DIRECTION_OPTIONS}
                value={
                  TIMING_DIRECTION_OPTIONS.find(
                    (o) => o.value === (config["timing_direction"] || "before"),
                  ) || null
                }
                onChange={(e, newValue) => handleConfigChange("timing_direction", newValue?.value)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} variant="outlined" />}
                disableClearable
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>

          <TextField
            label="Scheduled Send Time"
            type="time"
            variant="outlined"
            value={config["scheduled_time"] || ""}
            onChange={(e) => handleConfigChange("scheduled_time", e.target.value || "")}
            InputLabelProps={{ shrink: true }}
            helperText="Tenant-local time to actually send this reminder. Leave blank to send immediately when the cron runs."
          />

          <Autocomplete
            options={templates || []}
            value={selectedTemplate}
            onChange={(e, newValue) => handleTemplateChange(newValue)}
            getOptionLabel={(option) => option?.template_name || ""}
            getOptionDisabled={(option) =>
              isUnsupportedTemplate(option) && option?.template_id !== config["template_id"]
            }
            renderOption={(props, option) => (
              <Stack
                {...props}
                key={option?.id}
                direction="column"
                gap={0.5}
                sx={{
                  alignItems: "flex-start !important",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <Typography>{option?.template_name}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color={ColorsConstants.primaryGray40}>
                    {option?.category?.[0]}
                    {option?.category?.slice(1).toLowerCase()}
                  </Typography>
                  {isUnsupportedTemplate(option) && (
                    <AppChip
                      color="warning"
                      variant="outlined"
                      label={`${getTemplateType(option)} not supported`}
                    />
                  )}
                </Stack>
              </Stack>
            )}
            isOptionEqualToValue={(option, value) => option?.template_id === value?.template_id}
            renderInput={(params) => (
              <TextField {...params} label="Select Template" variant="outlined" required />
            )}
            noOptionsText="No approved templates found"
          />

          {isUnsupportedSelection && (
            <Alert severity="warning">
              EMI reminders support only text and image templates. This {selectedTemplateType}{" "}
              template will fail to send — please choose a different template.
            </Alert>
          )}

          {selectedTemplate && (
            <Stack spacing={1.5}>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                Variable Mapping
              </Typography>
              {placeholderCount === 0 && (
                <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.grayFontColor }}>
                  This template has no {"{{n}}"} placeholders — nothing to map.
                </Typography>
              )}
              {Array.from({ length: placeholderCount }, (_, index) => (
                <Autocomplete
                  key={index}
                  options={EMI_FIELD_OPTIONS}
                  value={
                    EMI_FIELD_OPTIONS.find((o) => o.value === variableMapping[index]) || null
                  }
                  onChange={(e, newValue) => handleMappingChange(index, newValue?.value)}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`{{${index + 1}}}`}
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  noOptionsText="No fields found"
                />
              ))}

              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mt: 1 }}>
                Preview
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#D9FDD3",
                  borderRadius: 2,
                  p: 2,
                  maxWidth: 320,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                }}
              >
                {isImageTemplate &&
                  (selectedTemplate?.image_url ? (
                    <Box
                      component="img"
                      src={selectedTemplate.image_url}
                      alt="Template header"
                      sx={{
                        width: "100%",
                        maxHeight: 180,
                        objectFit: "cover",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{ ...typography.bodySmall, color: ColorsConstants.grayFontColor, mb: 1 }}
                    >
                      Image header (sent from the template's saved image)
                    </Typography>
                  ))}
                <Typography sx={{ ...typography.bodyLarge, whiteSpace: "pre-wrap" }}>
                  {buildPreviewText(selectedTemplate, variableMapping)}
                </Typography>
              </Box>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default SendEmiReminderPreset;
